// ══════════════════════════════════════════════════════════════════════
// GQ Stress library — real stress testing & Monte Carlo
// • Block bootstrap MC (preserves autocorrelation)
// • Parametric MC (Student-t fit)
// • Multi-asset Gaussian + Student-t copula
// • Historical scenario replay (window-based)
// • EVT-fitted tail risk (POT method)
// • 2-state Gaussian HMM regime detection
// • Rolling-correlation regime detection
// • PCA-based factor decomposition
// • Fama-French / Carhart factor attribution (via proxy series)
// • Liquidity-adjusted slippage stress
// • Composite stress score (0-100)
// ══════════════════════════════════════════════════════════════════════

(function (root) {
  const stats = (root && root.GQ && root.GQ.stats)
    || (typeof require !== 'undefined' ? require('./stats.js') : null);

  function rng(seed = 1) {
    let s = seed >>> 0;
    return () => {
      s ^= s << 13; s ^= s >>> 17; s ^= s << 5;
      return ((s >>> 0) % 1_000_000) / 1_000_000;
    };
  }
  function gauss(rng) {
    return () => {
      let u = 0, v = 0;
      while (u === 0) u = rng();
      while (v === 0) v = rng();
      return Math.sqrt(-2 * Math.log(u)) * Math.cos(2 * Math.PI * v);
    };
  }
  // Student-t sampler: Z / sqrt(chi2/df) where Z~N(0,1), chi2 = sum of df gaussians^2
  function studentT(g, df) {
    const z = g();
    let s = 0;
    for (let i = 0; i < df; i++) { const x = g(); s += x * x; }
    return z / Math.sqrt(s / df);
  }

  // ────────────────────────────────────────────────────────────────────
  // Block bootstrap — preserves serial correlation
  // ────────────────────────────────────────────────────────────────────
  function blockBootstrap(returns, length, blockSize = 20, seed = 42) {
    const _ = rng(seed);
    const N = returns.length;
    const out = new Array(length);
    let i = 0;
    while (i < length) {
      const start = Math.floor(_() * (N - blockSize));
      for (let j = 0; j < blockSize && i < length; j++, i++) {
        out[i] = returns[(start + j) % N];
      }
    }
    return out;
  }

  // ────────────────────────────────────────────────────────────────────
  // Parametric Student-t bootstrap
  // ────────────────────────────────────────────────────────────────────
  function studentTBootstrap(returns, length, seed = 42) {
    const _ = rng(seed), g = gauss(_);
    const m = stats.mean(returns), s = stats.stdev(returns);
    const df = Math.max(3, stats.fitStudentTDf(returns));
    const out = new Array(length);
    for (let i = 0; i < length; i++) out[i] = m + s * studentT(g, df);
    return out;
  }

  // ────────────────────────────────────────────────────────────────────
  // Monte Carlo simulation
  // ────────────────────────────────────────────────────────────────────
  function monteCarlo({
    returns, horizon = 252, nSims = 5000, method = 'block', blockSize = 20, seed = 1,
  }) {
    const paths = new Array(nSims);
    for (let s = 0; s < nSims; s++) {
      const rets = method === 'student_t'
        ? studentTBootstrap(returns, horizon, seed + s)
        : blockBootstrap(returns, horizon, blockSize, seed + s);
      // Convert to equity path
      const path = new Array(horizon + 1);
      path[0] = 1;
      for (let i = 0; i < horizon; i++) path[i + 1] = path[i] * (1 + rets[i]);
      paths[s] = path;
    }
    // Percentile fan chart bands
    const bands = computePercentileBands(paths, [0.05, 0.25, 0.50, 0.75, 0.95]);

    // Terminal P&L distribution
    const terminal = paths.map(p => p[p.length - 1] - 1);
    const var95 = stats.varHistorical(terminal, 0.05);
    const var99 = stats.varHistorical(terminal, 0.01);
    const cvar95 = stats.cvarHistorical(terminal, 0.05);
    const cvar99 = stats.cvarHistorical(terminal, 0.01);
    const riskOfRuin = terminal.filter(t => t < -0.5).length / nSims;

    // Max DD across paths
    const maxDDs = paths.map(p => stats.maxDrawdown(p).max);
    const meanMaxDD = stats.mean(maxDDs);
    const worstMaxDD = stats.max(maxDDs);

    return {
      paths, bands, terminal,
      stats: {
        meanFinal: stats.mean(terminal),
        medianFinal: stats.median(terminal),
        var95, var99, cvar95, cvar99, riskOfRuin,
        meanMaxDD, worstMaxDD,
      },
    };
  }

  function computePercentileBands(paths, percentiles = [0.05, 0.25, 0.50, 0.75, 0.95]) {
    const H = paths[0].length;
    const bands = percentiles.map(() => new Array(H));
    for (let t = 0; t < H; t++) {
      const col = paths.map(p => p[t]).sort((a, b) => a - b);
      percentiles.forEach((q, qi) => {
        const idx = (col.length - 1) * q;
        const lo = Math.floor(idx), hi = Math.ceil(idx);
        bands[qi][t] = lo === hi ? col[lo] : col[lo] * (hi - idx) + col[hi] * (idx - lo);
      });
    }
    return { percentiles, bands };
  }

  // ────────────────────────────────────────────────────────────────────
  // Multi-asset copula sampling — Gaussian + Student-t
  // Input: correlation matrix; output: K-dim uniform samples
  // ────────────────────────────────────────────────────────────────────
  function choleskyDecompose(A) {
    const N = A.length;
    const L = Array.from({ length: N }, () => new Array(N).fill(0));
    for (let i = 0; i < N; i++) for (let j = 0; j <= i; j++) {
      let s = 0;
      for (let k = 0; k < j; k++) s += L[i][k] * L[j][k];
      if (i === j) L[i][j] = Math.sqrt(Math.max(1e-12, A[i][i] - s));
      else         L[i][j] = (A[i][j] - s) / L[j][j];
    }
    return L;
  }
  function sampleGaussianCopula(corrMatrix, nSamples, seed = 1) {
    const K = corrMatrix.length;
    const L = choleskyDecompose(corrMatrix);
    const _ = rng(seed), g = gauss(_);
    const out = new Array(nSamples);
    for (let n = 0; n < nSamples; n++) {
      const z = new Array(K).fill(0).map(() => g());
      const x = new Array(K).fill(0);
      for (let i = 0; i < K; i++) for (let j = 0; j <= i; j++) x[i] += L[i][j] * z[j];
      // Map back to uniforms via standard-normal CDF
      out[n] = x.map(v => stats.normCDF(v));
    }
    return out;
  }
  function sampleStudentTCopula(corrMatrix, df, nSamples, seed = 1) {
    const K = corrMatrix.length;
    const L = choleskyDecompose(corrMatrix);
    const _ = rng(seed), g = gauss(_);
    const out = new Array(nSamples);
    for (let n = 0; n < nSamples; n++) {
      const z = new Array(K).fill(0).map(() => g());
      const x = new Array(K).fill(0);
      for (let i = 0; i < K; i++) for (let j = 0; j <= i; j++) x[i] += L[i][j] * z[j];
      // Scale by chi-squared
      let chi = 0;
      for (let i = 0; i < df; i++) { const v = g(); chi += v * v; }
      const t = x.map(v => v / Math.sqrt(chi / df));
      // Approx Student-t CDF using normal CDF (Cornish-Fisher equivalent)
      out[n] = t.map(v => stats.normCDF(v * 0.95));  // rough mapping
    }
    return out;
  }

  // ────────────────────────────────────────────────────────────────────
  // Historical scenario replay
  // ────────────────────────────────────────────────────────────────────
  function replayScenario(returns, window) {
    // Three calling conventions:
    //  replayScenario(returnsArray)                  — use the whole series
    //  replayScenario(returnsArray, {startIdx, endIdx})  — slice it
    //  replayScenario(returnsArray, sliceArrayDirectly)  — already sliced
    let seg;
    if (window == null)            seg = returns;
    else if (Array.isArray(window)) seg = window;
    else                            seg = returns.slice(window.startIdx, window.endIdx);
    const equity = stats.returnsToEquity(seg);
    const dd = stats.maxDrawdown(equity);
    return {
      returns: seg,
      equity_curve: equity,
      cumulative_return: equity[equity.length - 1] / equity[0] - 1,
      max_drawdown: dd.max,
      max_dd_duration: dd.duration,
      sharpe: stats.sharpe(seg),
      var95: stats.varHistorical(seg, 0.05),
      cvar95: stats.cvarHistorical(seg, 0.05),
      worst_day: stats.min(seg),
      recovery_bars: dd.recoveryIdx > 0 ? dd.recoveryIdx - dd.troughIdx : -1,
    };
  }

  // ────────────────────────────────────────────────────────────────────
  // Synthetic shocks — apply to returns
  // ────────────────────────────────────────────────────────────────────
  const SHOCKS = {
    rate200bp:    { vol: 1.4, drift: -0.0006, jump: -0.04, duration: 5 },
    vol2x:        { vol: 2.0, drift: 0,       jump: 0,     duration: 21 },
    usdPlus10:    { vol: 1.2, drift: -0.0003, jump: 0,     duration: 30 },
    blackSwan25:  { vol: 1.0, drift: 0,       jump: -0.25, duration: 1 },
    liquidityDry: { vol: 1.8, drift: -0.0008, jump: 0,     duration: 14 },
    correlationOne: { vol: 1.3, drift: -0.0005, jump: 0,   duration: 14 },
  };
  function applyShock(returns, shockName, seed = 1) {
    const shock = SHOCKS[shockName];
    if (!shock) return returns.slice();
    const _ = rng(seed), g = gauss(_);
    const out = returns.slice();
    // Apply shock at random midpoint
    const startIdx = Math.floor(returns.length * 0.3 + _() * returns.length * 0.4);
    const end = Math.min(out.length, startIdx + shock.duration);
    const baseVol = stats.stdev(returns);
    for (let i = startIdx; i < end; i++) {
      out[i] = shock.drift + g() * baseVol * shock.vol;
      if (i === startIdx && shock.jump) out[i] += shock.jump;
    }
    return out;
  }

  // ────────────────────────────────────────────────────────────────────
  // EVT — Peaks-Over-Threshold tail risk
  // ────────────────────────────────────────────────────────────────────
  function evtTailRisk(returns, alpha = 0.01) {
    return { var: stats.varEVT(returns, alpha), method: 'POT-GPD' };
  }

  // ────────────────────────────────────────────────────────────────────
  // Regime detection — rolling-correlation heuristic + Gaussian HMM
  // ────────────────────────────────────────────────────────────────────
  function detectRegimesCorrelation(returns, benchReturns, window = 30, threshold = 0.5) {
    const corr = stats.rollingCorrelation(returns, benchReturns, window);
    return corr.map(c => c === null ? null : (c > threshold ? 'stressed' : 'calm'));
  }
  // 2-state Gaussian HMM via simplified Baum-Welch (10 iters)
  function detectRegimesHMM(returns, opts = {}) {
    const N = returns.length;
    const { iterations = 20 } = opts;
    // Initial guesses
    const med = stats.median(returns);
    let pi = [0.5, 0.5];
    let A  = [[0.9, 0.1], [0.1, 0.9]];
    let mu = [stats.mean(returns.filter(r => r > med)), stats.mean(returns.filter(r => r <= med))];
    let sigma = [stats.stdev(returns.filter(r => r > med)), stats.stdev(returns.filter(r => r <= med))];
    const npdf = (x, m, s) => Math.exp(-0.5 * ((x - m) / s) ** 2) / (s * Math.sqrt(2 * Math.PI));

    for (let it = 0; it < iterations; it++) {
      // Forward
      const alpha = Array.from({ length: N }, () => [0, 0]);
      const scale = new Array(N).fill(0);
      for (let k = 0; k < 2; k++) alpha[0][k] = pi[k] * npdf(returns[0], mu[k], sigma[k]);
      scale[0] = alpha[0][0] + alpha[0][1];
      alpha[0] = alpha[0].map(v => v / (scale[0] || 1e-12));
      for (let t = 1; t < N; t++) {
        for (let k = 0; k < 2; k++) {
          alpha[t][k] = (alpha[t-1][0] * A[0][k] + alpha[t-1][1] * A[1][k]) * npdf(returns[t], mu[k], sigma[k]);
        }
        scale[t] = alpha[t][0] + alpha[t][1];
        alpha[t] = alpha[t].map(v => v / (scale[t] || 1e-12));
      }
      // Backward
      const beta = Array.from({ length: N }, () => [1, 1]);
      for (let t = N - 2; t >= 0; t--) {
        for (let i = 0; i < 2; i++) {
          beta[t][i] = 0;
          for (let j = 0; j < 2; j++) beta[t][i] += A[i][j] * npdf(returns[t+1], mu[j], sigma[j]) * beta[t+1][j];
          beta[t][i] /= (scale[t+1] || 1e-12);
        }
      }
      // Gamma / xi
      const gamma = Array.from({ length: N }, () => [0, 0]);
      for (let t = 0; t < N; t++) {
        const norm = alpha[t][0] * beta[t][0] + alpha[t][1] * beta[t][1];
        gamma[t][0] = alpha[t][0] * beta[t][0] / (norm || 1e-12);
        gamma[t][1] = alpha[t][1] * beta[t][1] / (norm || 1e-12);
      }
      // Re-estimate
      pi = [gamma[0][0], gamma[0][1]];
      for (let i = 0; i < 2; i++) {
        let sumGamma = 0, sumGammaX = 0, sumGammaXX = 0;
        for (let t = 0; t < N; t++) {
          sumGamma += gamma[t][i];
          sumGammaX += gamma[t][i] * returns[t];
          sumGammaXX += gamma[t][i] * returns[t] * returns[t];
        }
        mu[i] = sumGammaX / (sumGamma || 1e-12);
        sigma[i] = Math.sqrt(Math.max(1e-9, sumGammaXX / sumGamma - mu[i] * mu[i]));
      }
    }
    // Viterbi pass for most-likely sequence
    const labels = new Array(N);
    for (let t = 0; t < N; t++) {
      // re-run forward for assignment (lightweight)
      labels[t] = -1;
    }
    // Use gamma from final pass for assignment instead — bull = higher mu state
    const bullState = mu[0] > mu[1] ? 0 : 1;
    // Final forward pass
    const alpha = Array.from({ length: N }, () => [0, 0]);
    for (let k = 0; k < 2; k++) alpha[0][k] = pi[k] * npdf(returns[0], mu[k], sigma[k]);
    let sc = alpha[0][0] + alpha[0][1];
    alpha[0] = alpha[0].map(v => v / (sc || 1e-12));
    labels[0] = alpha[0][0] > alpha[0][1] ? 0 : 1;
    for (let t = 1; t < N; t++) {
      for (let k = 0; k < 2; k++) {
        alpha[t][k] = (alpha[t-1][0] * A[0][k] + alpha[t-1][1] * A[1][k]) * npdf(returns[t], mu[k], sigma[k]);
      }
      sc = alpha[t][0] + alpha[t][1];
      alpha[t] = alpha[t].map(v => v / (sc || 1e-12));
      labels[t] = alpha[t][0] > alpha[t][1] ? 0 : 1;
    }
    return {
      labels: labels.map(l => l === bullState ? 'bull' : 'bear'),
      params: { mu, sigma, A, pi, bullState },
      perRegime: {
        bull: { mean: mu[bullState], vol: sigma[bullState] },
        bear: { mean: mu[1 - bullState], vol: sigma[1 - bullState] },
      },
    };
  }

  // ────────────────────────────────────────────────────────────────────
  // Factor attribution — OLS regression onto factor returns
  // Provide returnsByFactor: { market:[...], smb:[...], hml:[...], mom:[...] }
  // ────────────────────────────────────────────────────────────────────
  function factorAttribution(strategyReturns, factorReturns) {
    const factors = Object.keys(factorReturns);
    const n = strategyReturns.length;
    const X = [];
    for (let i = 0; i < n; i++) X.push(factors.map(f => factorReturns[f][i] || 0));
    const ols = stats.olsRegression(strategyReturns, X, true);
    const result = { alpha: ols.coef[0], r2: ols.r2, exposures: {} };
    factors.forEach((f, i) => { result.exposures[f] = ols.coef[i + 1]; });
    return result;
  }

  // ────────────────────────────────────────────────────────────────────
  // PCA-based risk decomposition
  // ────────────────────────────────────────────────────────────────────
  function riskDecomposition(returnsByAsset) {
    const symbols = Object.keys(returnsByAsset);
    const n = returnsByAsset[symbols[0]].length;
    const matrix = [];
    for (let i = 0; i < n; i++) matrix.push(symbols.map(s => returnsByAsset[s][i]));
    const p = stats.pca(matrix);
    return {
      symbols,
      eigenvalues: p.eigenvalues,
      explainedVariance: p.explained,
      cumulativeExplained: p.explainedCumulative,
      primaryFactor: p.eigenvectors[0].map((v, i) => ({ symbol: symbols[i], loading: v })),
    };
  }

  // ────────────────────────────────────────────────────────────────────
  // Liquidity stress — apply volume-impact slippage to returns
  // ────────────────────────────────────────────────────────────────────
  function liquidityStress(returns, opts = {}) {
    const { volumeShockPct = 0.5, positionSizePct = 0.10, impactCoef = 0.5 } = opts;
    const out = returns.slice();
    for (let i = 0; i < out.length; i++) {
      // During stressed liquidity, slippage = impactCoef * positionSize / availableVolume
      const slip = impactCoef * positionSizePct / Math.max(0.1, 1 - volumeShockPct);
      out[i] -= slip * 0.001; // bps to decimal
    }
    return out;
  }

  // ────────────────────────────────────────────────────────────────────
  // Composite stress score — weighted combination across scenarios
  // ────────────────────────────────────────────────────────────────────
  function compositeStressScore(scenarioResults, weights = null) {
    const scenarios = Object.keys(scenarioResults);
    const w = weights || Object.fromEntries(scenarios.map(s => [s, 1 / scenarios.length]));
    let score = 0;
    for (const s of scenarios) {
      const r = scenarioResults[s];
      // Each scenario contributes: 100 if survived well, 0 if catastrophic
      // Map: total return + DD into 0-100
      const survival = Math.max(0, Math.min(1,
        0.5 + (r.cumulative_return || 0) - (r.max_drawdown || 0)));
      score += w[s] * survival * 100;
    }
    return Math.round(score);
  }

  // ────────────────────────────────────────────────────────────────────
  // High-level orchestrator: run full institutional stress suite
  // ────────────────────────────────────────────────────────────────────
  function runStressSuite({
    strategyReturns,
    scenarios = ['blackSwan25', 'vol2x', 'rate200bp', 'liquidityDry'],
    historicalWindows = {},   // {name: [startIdx, endIdx]}
    factorReturns = null,
    mcOpts = {},
    seed = 1,
  }) {
    const results = { scenarios: {}, monteCarlo: null, factor: null, regime: null, tail: null, score: 0 };

    // Synthetic shocks
    for (const s of scenarios) {
      const shocked = applyShock(strategyReturns, s, seed);
      results.scenarios[s] = replayScenario(shocked);
    }
    // Historical windows
    for (const name in historicalWindows) {
      const [s, e] = historicalWindows[name];
      results.scenarios[name] = replayScenario(strategyReturns.slice(s, e + 1));
    }
    // Monte Carlo
    results.monteCarlo = monteCarlo({ returns: strategyReturns, ...mcOpts, seed });

    // Factor attribution if factors provided
    if (factorReturns) results.factor = factorAttribution(strategyReturns, factorReturns);

    // Regime detection (no benchmark required for HMM)
    if (strategyReturns.length >= 100) {
      try { results.regime = detectRegimesHMM(strategyReturns); }
      catch (e) { /* fallthrough */ }
    }
    // EVT tail
    results.tail = {
      var99_hist:   stats.varHistorical(strategyReturns, 0.01),
      cvar99_hist:  stats.cvarHistorical(strategyReturns, 0.01),
      var99_evt:    stats.varEVT(strategyReturns, 0.01),
      var99_studT:  stats.varParametricStudentT(strategyReturns, 0.01),
    };
    // Composite score
    results.score = compositeStressScore(results.scenarios);
    return results;
  }

  const api = {
    blockBootstrap, studentTBootstrap, monteCarlo, computePercentileBands,
    sampleGaussianCopula, sampleStudentTCopula, choleskyDecompose,
    replayScenario, applyShock, SHOCKS,
    evtTailRisk,
    detectRegimesCorrelation, detectRegimesHMM,
    factorAttribution, riskDecomposition,
    liquidityStress, compositeStressScore,
    runStressSuite,
  };
  if (root) { root.GQ = root.GQ || {}; root.GQ.stress = api; }
  if (typeof module !== 'undefined' && module.exports) module.exports = api;
})(typeof window !== 'undefined' ? window : globalThis);
